import { Injectable } from '@angular/core';
import {
  ProvetBreaker,
  ProvetBreakerState,
  ProvetBucket,
  ProvetCacheScope,
  ProvetCallEvent,
  ProvetClientKind,
  ProvetClientSplit,
  ProvetDiagnosticsConfig,
  ProvetDiagnosticsSnapshot,
  ProvetDiagnosticsTotals,
  ProvetEventOrigin,
  ProvetLimitOverride,
} from '../../domain/models/provet-diagnostics.model';
import {
  ProvetBreakerDto,
  ProvetBucketDto,
  ProvetCacheScopeDto,
  ProvetCallEventDto,
  ProvetClientSplitDto,
  ProvetDiagnosticsConfigDto,
  ProvetDiagnosticsSnapshotDto,
  ProvetDiagnosticsTotalsDto,
  ProvetLimitOverrideDto,
} from '../dtos/provet-diagnostics.dto';

const SECOND_SERIES_LENGTH = 60;

const EMPTY_SPLIT: ProvetClientSplit = { interactive: 0, roster: 0, background: 0 };

const EMPTY_TOTALS: ProvetDiagnosticsTotals = {
  requestsLastMinute: 0,
  attemptsTotal: 0,
  retriesTotal: 0,
  rateLimitedTotal: 0,
  queueSaturatedTotal: 0,
  cacheHitRatePercent: 0,
  callsAvoided: 0,
};

const EMPTY_CONFIG: ProvetDiagnosticsConfig = {
  windowSeconds: 0,
  segmentsPerWindow: 0,
  queueLimit: 0,
  defaultPermitLimit: 0,
  eventCapacity: 0,
  overrides: [],
};

@Injectable()
export class ProvetDiagnosticsMapper {
  public toDomain(dto: ProvetDiagnosticsSnapshotDto): ProvetDiagnosticsSnapshot {
    return {
      serverTime: toDate(dto.ServerTimeUtc),
      sessionStarted: toDate(dto.SessionStartedUtc),
      processId: dto.ProcessId ?? '',
      tenant: dto.Tenant ?? null,
      detailEnabled: dto.DetailEnabled === true,
      nextSince: toNumber(dto.NextSince),
      droppedEvents: toNumber(dto.DroppedEvents),
      totals: this.toTotals(dto.Totals),
      buckets: (dto.Buckets ?? []).map((bucket) => this.toBucket(bucket)),
      breakers: (dto.Breakers ?? []).map((breaker) => this.toBreaker(breaker)),
      cacheScopes: (dto.CacheScopes ?? []).map((scope) => this.toCacheScope(scope)),
      events: (dto.Events ?? []).map((event) => this.toEvent(event)),
      config: this.toConfig(dto.Config),
    };
  }

  private toTotals(dto: ProvetDiagnosticsTotalsDto | null): ProvetDiagnosticsTotals {
    if (!dto) return EMPTY_TOTALS;

    return {
      requestsLastMinute: toNumber(dto.RequestsLastMinute),
      attemptsTotal: toNumber(dto.AttemptsTotal),
      retriesTotal: toNumber(dto.RetriesTotal),
      rateLimitedTotal: toNumber(dto.RateLimitedTotal),
      queueSaturatedTotal: toNumber(dto.QueueSaturatedTotal),
      cacheHitRatePercent: toNumber(dto.CacheHitRatePercent),
      callsAvoided: toNumber(dto.CallsAvoided),
    };
  }

  private toBucket(dto: ProvetBucketDto): ProvetBucket {
    return {
      key: dto.Key ?? '',
      endpoint: dto.Endpoint ?? '',
      permitLimit: toNumber(dto.PermitLimit),
      permitsUsed: toNumber(dto.PermitsUsed),
      attempts: toNumber(dto.Attempts),
      queuedCount: toNumber(dto.QueuedCount),
      limitSource: dto.LimitSource ?? '',
      p50Ms: toNumber(dto.P50Ms),
      p95Ms: toNumber(dto.P95Ms),
      maxMs: toNumber(dto.MaxMs),
      retries: toNumber(dto.Retries),
      rateLimited: toNumber(dto.RateLimited),
      byClient: this.toSplit(dto.ByClient),
      secondSeries: toSecondSeries(dto.SecondSeries),
    };
  }

  private toSplit(dto: ProvetClientSplitDto | null): ProvetClientSplit {
    if (!dto) return EMPTY_SPLIT;

    return {
      interactive: toNumber(dto.Interactive),
      roster: toNumber(dto.Roster),
      background: toNumber(dto.Background),
    };
  }

  private toBreaker(dto: ProvetBreakerDto): ProvetBreaker {
    return {
      client: toClientKind(dto.Client),
      state: toBreakerState(dto.State),
      changedAt: toDate(dto.ChangedAtUtc),
      reopensInSeconds: toNullableNumber(dto.ReopensInSeconds),
    };
  }

  private toCacheScope(dto: ProvetCacheScopeDto): ProvetCacheScope {
    return {
      scope: dto.Scope ?? '',
      freshHits: toNumber(dto.FreshHits),
      staleHits: toNumber(dto.StaleHits),
      misses: toNumber(dto.Misses),
      coalesced: toNumber(dto.Coalesced),
      freshSeconds: toNumber(dto.FreshSeconds),
      staleSeconds: toNumber(dto.StaleSeconds),
    };
  }

  private toEvent(dto: ProvetCallEventDto): ProvetCallEvent {
    return {
      seq: toNumber(dto.Seq),
      at: toDate(dto.AtUtc),
      endpoint: dto.Endpoint ?? '',
      client: toClientKind(dto.Client),
      attempt: Math.max(1, toNumber(dto.Attempt)),
      queueWaitMs: toNumber(dto.QueueWaitMs),
      durationMs: toNullableNumber(dto.DurationMs),
      statusCode: toNullableNumber(dto.StatusCode),
      retryAfterSeconds: toNullableNumber(dto.RetryAfterSeconds),
      leaseAcquired: dto.LeaseAcquired === true,
      correlationId: emptyToNull(dto.CorrelationId),
      route: emptyToNull(dto.Route),
      trigger: emptyToNull(dto.Trigger),
      origin: toOrigin(dto.Origin),
    };
  }

  private toConfig(dto: ProvetDiagnosticsConfigDto | null): ProvetDiagnosticsConfig {
    if (!dto) return EMPTY_CONFIG;

    return {
      windowSeconds: toNumber(dto.WindowSeconds),
      segmentsPerWindow: toNumber(dto.SegmentsPerWindow),
      queueLimit: toNumber(dto.QueueLimit),
      defaultPermitLimit: toNumber(dto.DefaultPermitLimit),
      eventCapacity: toNumber(dto.EventCapacity),
      overrides: (dto.Overrides ?? []).map((override) => toOverride(override)),
    };
  }
}

function toOverride(dto: ProvetLimitOverrideDto): ProvetLimitOverride {
  return { fragment: dto.Fragment ?? '', permitLimit: toNumber(dto.PermitLimit) };
}

function toNumber(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function toNullableNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function emptyToNull(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// The wire pads/truncates to 60 on the server, but a shorter series would silently misalign the
// sparkline's "one pixel column = one second" reading, so it is normalized here too.
function toSecondSeries(values: number[] | null | undefined): readonly number[] {
  const source = values ?? [];
  if (source.length >= SECOND_SERIES_LENGTH) {
    return source.slice(source.length - SECOND_SERIES_LENGTH).map((value) => toNumber(value));
  }

  const padding = new Array<number>(SECOND_SERIES_LENGTH - source.length).fill(0);
  return [...padding, ...source.map((value) => toNumber(value))];
}

function toClientKind(value: string | null | undefined): ProvetClientKind {
  switch ((value ?? '').toLowerCase()) {
    case 'interactive':
      return 'interactive';
    case 'roster':
      return 'roster';
    default:
      return 'background';
  }
}

function toBreakerState(value: string | null | undefined): ProvetBreakerState {
  switch ((value ?? '').toLowerCase()) {
    case 'open':
      return 'open';
    case 'halfopen':
      return 'halfOpen';
    default:
      return 'closed';
  }
}

function toOrigin(value: string | null | undefined): ProvetEventOrigin {
  switch ((value ?? '').toLowerCase()) {
    case 'route':
      return 'route';
    case 'marker':
      return 'marker';
    case 'job':
      return 'job';
    default:
      return 'unknown';
  }
}
